import React, { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";

import {
  handleSuccessPurchase,
  setPending,
} from "@web-solutions/core/store/billing/actions";
import { useRemoteConfig } from "@web-solutions/base-app/hooks/use-remote-config";

import { PaymentSystem } from "..";

import { installTruegateApplePayEmailCapture } from "./apple-pay-email-capture";

import type { SdkInstance } from "./truegate-sdk";

export const ApplePayTruegateContainer: React.FC<any> = ({
  options,
  email,
  product,
  onSubmit,
  onSuccess,
  onReady,
  onClick,
  onError,
}) => {

  const productId = product?.truegateId || product?.productCode || "no product";
  const intent =
    options?.orderDetails?.products?.[productId] ||
    options?.orderDetails?.one_time_products?.[productId] ||
    options?.orderDetails;

  const dispatch = useDispatch();
  const { requestWalletEmail, requestApplePayEmail } = useRemoteConfig();

  // `requestApplePayEmail` is the pre-rename key still configured on most
  // funnels; `requestWalletEmail` is `null` until one is migrated.
  const requestEmail = requestWalletEmail ?? requestApplePayEmail;

  const sdkInstanceRef = useRef<SdkInstance | null>(null);
  const intentIdRef = useRef<string | null>(null);
  const uninstallEmailCaptureRef = useRef<(() => void) | null>(null);

  // Truegate transactions are single-use; a stale instance keeps its window
  // listeners and reacts to events of the next one, so it must be destroyed
  // before a new instance is initialized for a fresh transaction.
  const destroySdkInstance = () => {
    try {
      sdkInstanceRef.current?.destroy?.();
    } catch (error) {
      console.warn("Truegate SDK destroy failed", error);
    }
    sdkInstanceRef.current = null;

    uninstallEmailCaptureRef.current?.();
    uninstallEmailCaptureRef.current = null;
  };

  useEffect(() => {
    const currentIntentId = intent?.transactionId;
    if (intentIdRef.current === currentIntentId && sdkInstanceRef.current) {
      return;
    }

    destroySdkInstance();
    intentIdRef.current = currentIntentId;

    const init = async () => {
      // Deferred-email flow: make the native sheet ask for the payer's email and
      // stash what it returns, so the success handler can attach it and skip the
      // email-after-payment page — the same `window` channel the Stripe
      // ApplePayButton uses. Set on every authorization (incl. cleared) so a
      // stale value can't leak into a later card payment.
      if (requestEmail) {
        uninstallEmailCaptureRef.current = installTruegateApplePayEmailCapture(
          (payerEmail) => {
            (window as any).__apple_pay_payer_email__ = payerEmail || undefined;
          }
        );
      }

      // Unique per transaction: the id routes postMessage events between the
      // SDK and its iframes, so it must never be shared between instances.
      const ID = `APPLE_PAY_TRUEGATE_${intent.transactionId}`;

      //@ts-ignore
      const sdkInstance: SdkInstance = new window.truegateSdk({
        id: ID,
        transactionId: intent.transactionId,
        env: process.env.REACT_APP_TRUEGATE_ENV as any,
      });
      sdkInstanceRef.current = sdkInstance;

      sdkInstance.on("APPLE_PAY_READY" as any, (payload) => {
        onReady({
          paymentSystem: PaymentSystem.TRUEGATE,
        });
      });

      sdkInstance.on("APPLE_PAY_BUTTON_CLICK" as any, (payload) => {
        onClick({
          paymentSystem: PaymentSystem.TRUEGATE,
        });
      });

      sdkInstance.on("PAYMENT_STATUS" as any, (payload: any) => {
        if (
          payload.details.status === "FAILED" &&
          payload.channel === "APPLE_PAY"
        ) {
          dispatch(setPending(false));
          onError({
            paymentSystem: PaymentSystem.TRUEGATE,
            method: "applepay",
            amount: product.amount,
            currency: product.currency,
            email,
          });
          console.log("ERROR", payload);
          return;
        }
        if (
          payload.details.status === "PENDING" &&
          payload.channel === "APPLE_PAY"
        ) {
          dispatch(setPending(true));
          onSubmit({
            paymentSystem: PaymentSystem.TRUEGATE,
            method: "applepay",
            amount: product.amount,
            currency: product.currency,
            email,
            subscription_id: productId,
            transaction_id: intent.transactionId,
            price_id: productId,
          });
        }
        if (
          payload.details.status === "SUCCESS" &&
          payload.channel === "APPLE_PAY"
        ) {
          dispatch(
            handleSuccessPurchase({
              paymentSystem: PaymentSystem.TRUEGATE,
              email,
              method: "applepay",
              amount: product.amount,
              currency: product.currency,
              subscription_id: productId,
              transaction_id: intent.transactionId,
              price_id: productId,
            } as any)
          );
          onSuccess({
            paymentSystem: PaymentSystem.TRUEGATE,
            method: "applepay",
            amount: product.amount,
            email,
            currency: product.currency,
            subscription_id: productId,
            transaction_id: intent.transactionId,
            price_id: productId,
          });
          dispatch(setPending(false));
        }
      });

      try {
        await sdkInstance.init();
        await sdkInstance.initApplePay({
          id: `truegate-apple-pay-button-${intent.transactionId}`,
          buttonOptions: { HEIGHT: "52px" },
        });
      } catch (error) {
        if (sdkInstanceRef.current === sdkInstance) {
          console.error("Truegate Apple Pay init failed", error);
        }
        // Aborted init of a superseded/destroyed instance — ignore.
      }
    };

    if (intent && currentIntentId) {
      init();
    }

    return () => {
      destroySdkInstance();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intent?.transactionId]);

  if (!intent) {
    return null;
  }

  return (
    <div
      key={`truegate-apple-pay-button-${intent.transactionId}`}
      id={`truegate-apple-pay-button-${intent.transactionId}`}
      style={{ width: "100%", height: "52px", border: "none" }}
    />
  );
};
